import { motion } from "framer-motion";
import { PhoneCall, Handshake, Banknote } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { IMAGES } from "@/lib/constants";

const ICONS = [PhoneCall, Handshake, Banknote];

export const HowItWorks = () => {
    const { t } = useLang();

    return (
        <section id="how-it-works" data-testid="how-it-works-section" className="relative py-20 lg:py-28">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 28 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.7 }}
                    className="max-w-2xl"
                >
                    <span className="text-xs font-extrabold uppercase tracking-[0.25em] text-volt">
                        {t("how.eyebrow")}
                    </span>
                    <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white">
                        {t("how.h")}
                    </h2>
                    <p className="mt-4 text-base leading-relaxed text-slate-400">{t("how.sub")}</p>
                </motion.div>

                <div className="mt-12 grid gap-5 md:grid-cols-3">
                    {t("how.steps").map((step, i) => {
                        const Icon = ICONS[i];
                        return (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 36 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{ duration: 0.6, delay: i * 0.15 }}
                                data-testid={`how-step-${i}`}
                                className="group relative overflow-hidden rounded-3xl border border-white/10 bg-surface p-8 transition-all hover:-translate-y-1 hover:border-volt/40 hover:shadow-[0_20px_60px_rgba(212,255,0,0.08)]"
                            >
                                <span className="absolute -top-5 -right-2 font-display text-[7rem] font-black leading-none text-white/[0.045] select-none transition-colors group-hover:text-volt/10">
                                    0{i + 1}
                                </span>
                                <span className="grid h-12 w-12 place-items-center rounded-2xl border border-volt/30 bg-volt/10">
                                    <Icon className="h-6 w-6 text-volt" />
                                </span>
                                <h3 className="mt-6 font-display text-xl sm:text-2xl font-extrabold text-white">
                                    {step.t}
                                </h3>
                                <p className="mt-3 text-sm leading-relaxed text-slate-400">{step.d}</p>
                            </motion.div>
                        );
                    })}
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.7 }}
                    className="relative mt-12 overflow-hidden rounded-3xl border border-white/10"
                >
                    <img src={IMAGES.tow} alt="" loading="lazy" className="h-64 sm:h-72 w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-r from-obsidian/90 via-obsidian/40 to-transparent" />
                    <span className="absolute bottom-5 left-5 sm:left-8 inline-flex items-center gap-2 rounded-full border border-volt/40 bg-obsidian/80 px-4 py-2 text-xs sm:text-sm font-extrabold text-volt backdrop-blur">
                        {t("how.band")}
                    </span>
                </motion.div>
            </div>
        </section>
    );
};

export default HowItWorks;
